import * as React from 'react';

import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  lighten,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

import { FlightOperationNotificationVersionGroup } from '../../core/api/clients';
import { getColor } from '../../core/colors';
import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';

import FlightOperationNotificationVersionsTable from './FlightOperationNotificationVersionsTable';

interface FlightOperationNotificationVersionsProps {
  versionGroups: FlightOperationNotificationVersionGroup[];
  requestedVersion: number | undefined;
}

type VersionInformation = {
  selectedVersion: number | undefined,
  versionChipLabel: string | undefined,
  latestVersion: number | undefined,
  isShowingChip: boolean,
};

function FlightOperationNotificationVersions({ versionGroups, requestedVersion }: FlightOperationNotificationVersionsProps) {
  const { metadata } = useAuthorizedFormData();

  const [isVersionDialogOpen, setIsVersionDialogOpen] = React.useState<boolean>(false);
  const [versionInformation, setVersionInformation] = React.useState<VersionInformation>({} as VersionInformation);

  React.useEffect(() => {
    if (Array.isArray(versionGroups) && versionGroups.length > 0) {
      const allVersionNumbers = versionGroups.map((g) => g.version?.versionNumber ?? 0);
      const maxVersionNumber = Math.max(...allVersionNumbers);
      const actualRequestedVersion = requestedVersion !== undefined ? requestedVersion : maxVersionNumber;

      if (allVersionNumbers.includes(actualRequestedVersion)) {
        setVersionInformation({
          selectedVersion: actualRequestedVersion,
          latestVersion: maxVersionNumber,
          isShowingChip: true,
          versionChipLabel: actualRequestedVersion !== maxVersionNumber
            ? `${actualRequestedVersion} of ${maxVersionNumber}`
            : `${actualRequestedVersion}`,
        });
      }
    }
  }, [versionGroups, requestedVersion]);

  const handleVersionChipClick = () => setIsVersionDialogOpen((prevState) => !prevState);
  const handleDialogClose = () => setIsVersionDialogOpen(false);

  if (versionInformation.isShowingChip) {
    return (
      <React.Fragment>
        <Box marginRight={1}>
          <Chip
            variant="outlined"
            onClick={handleVersionChipClick}
            label={`Version ${versionInformation.versionChipLabel}`}
          />
        </Box>
        <Dialog
          fullWidth
          maxWidth="lg"
          onClose={handleDialogClose}
          open={isVersionDialogOpen}
        >
          <DialogTitle sx={{ backgroundColor: lighten(getColor(metadata?.color)[200], 0.8) }}>Versions</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers sx={{ padding: 0, maxHeight: 644 }}>
            <FlightOperationNotificationVersionsTable
              versionGroups={versionGroups}
              latestVersion={versionInformation.latestVersion}
              selectedVersion={versionInformation.selectedVersion}
            />
          </DialogContent>
        </Dialog>
      </React.Fragment>
    );
  }

  return null;
}

export default FlightOperationNotificationVersions;
